import { useState, useEffect, useRef } from 'react'
import { onboardingApi } from '../services/api'
import { useLang } from '../../i18n/LanguageContext'

// Countries a tenant can be located in (drives VAT rules etc.) — names come
// from the active language's dictionary.
const COUNTRIES = [
  { code: 'NO', flag: '🇳🇴' },
  { code: 'SE', flag: '🇸🇪' },
  { code: 'DK', flag: '🇩🇰' },
]

const MAX_LOGO_BYTES = 2 * 1024 * 1024 // must match the backend cap

export default function Step2Organization({ data, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState({})
  const [plans, setPlans] = useState([])
  const fileInputRef = useRef(null)
  const { t } = useLang()
  const layouts = t('onboarding.layouts')
  const countryNames = t('onboarding.countryNames')

  useEffect(() => {
    let cancelled = false

    onboardingApi.getSubscriptionPlans()
      .then((result) => {
        if (!cancelled) {
          setPlans(Array.isArray(result) ? result : [])
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlans([])
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Auto-generate slug from tenant name
  useEffect(() => {
    if (data.tenantName && !data.slugManual) {
      onChange({ slug: onboardingApi.slugify(data.tenantName) })
    }
  }, [data.tenantName])

  const validate = () => {
    const e = {}
    if (!data.tenantName?.trim()) e.tenantName = t('onboarding.orgNameRequired')
    if (!data.slug?.trim()) e.slug = t('onboarding.slugRequired')
    else if (!/^[a-z0-9-]+$/.test(data.slug)) e.slug = t('onboarding.slugFormat')
    else if (data.slug.length < 2) e.slug = t('onboarding.slugMin')
    if (!data.shortName?.trim()) e.shortName = t('onboarding.shortNameRequired')
    else if (data.shortName.length > 10) e.shortName = t('onboarding.shortNameMax')
    else if (!/^[A-ZÆØÅa-zæøå0-9]+$/.test(data.shortName)) e.shortName = t('onboarding.shortNameFormat')
    if (data.customDomain?.trim()) {
      const normalizedDomain = data.customDomain.trim().toLowerCase()
      if (/^https?:\/\//.test(normalizedDomain)) {
        e.customDomain = t('onboarding.customDomainNoProtocol')
      } else if (normalizedDomain.includes('/')) {
        e.customDomain = t('onboarding.customDomainNoPath')
      } else if (!/^[a-z0-9]([a-z0-9\-.]{0,251}[a-z0-9])?$/.test(normalizedDomain)) {
        e.customDomain = t('onboarding.customDomainInvalid')
      }
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleNext = () => {
    if (validate()) onNext()
  }

  const handleSlugChange = (value) => {
    const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    onChange({ slug: clean, slugManual: true })
  }

  const handleLogoFile = (file) => {
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logo: t('onboarding.logoNotImage') }))
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setErrors(prev => ({ ...prev, logo: t('onboarding.logoTooBig') }))
      return
    }
    setErrors(prev => {
      const { logo, ...rest } = prev
      return rest
    })
    const reader = new FileReader()
    reader.onload = () => onChange({ logoFile: file, logoPreview: reader.result, logoUrl: '' })
    reader.readAsDataURL(file)
  }

  const clearLogo = () => {
    onChange({ logoFile: null, logoPreview: '' })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const selectedCountry = COUNTRIES.find(c => c.code === (data.countryCode || 'NO')) || COUNTRIES[0]

  return (
    <div className="wizard__body">
      <h1 className="wizard__title">{t('onboarding.step2Title')}</h1>
      <p className="wizard__subtitle">
        {t('onboarding.step2Subtitle')}
      </p>

      <div className="form-group">
        <label className="form-label form-label--required">{t('onboarding.orgName')}</label>
        <input
          className={`form-input ${errors.tenantName ? 'form-input--error' : ''}`}
          type="text"
          placeholder={t('onboarding.orgNamePlaceholder')}
          value={data.tenantName || ''}
          onChange={(e) => onChange({ tenantName: e.target.value })}
          autoFocus
        />
        {errors.tenantName && <div className="form-error">{errors.tenantName}</div>}
      </div>

      <div className="form-row form-row--2">
        <div className="form-group">
          <label className="form-label form-label--required">{t('onboarding.slug')}</label>
          <input
            className={`form-input ${errors.slug ? 'form-input--error' : ''}`}
            type="text"
            placeholder="din-org"
            value={data.slug || ''}
            onChange={(e) => handleSlugChange(e.target.value)}
          />
          {errors.slug && <div className="form-error">{errors.slug}</div>}
          {data.slug && !errors.slug && (
            <div className="slug-preview">
              <span className="slug-preview__value">{data.slug}</span>.communitynord.com
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label form-label--required">{t('onboarding.shortName')}</label>
          <input
            className={`form-input ${errors.shortName ? 'form-input--error' : ''}`}
            type="text"
            placeholder="ORG"
            maxLength={10}
            value={data.shortName || ''}
            onChange={(e) => onChange({ shortName: e.target.value.toUpperCase() })}
          />
          {errors.shortName && <div className="form-error">{errors.shortName}</div>}
          <div className="form-hint">{t('onboarding.shortNameHint')}</div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('onboarding.customDomain')}</label>
        <input
          className={`form-input ${errors.customDomain ? 'form-input--error' : ''}`}
          type="text"
          placeholder={t('onboarding.customDomainPlaceholder')}
          value={data.customDomain || ''}
          onChange={(e) => onChange({ customDomain: e.target.value.toLowerCase() })}
        />
        {errors.customDomain && <div className="form-error">{errors.customDomain}</div>}
        <div className="form-hint">{t('onboarding.customDomainHint')}</div>
      </div>

      <div className="form-row form-row--2">
        <div className="form-group">
          <label className="form-label form-label--required">{t('onboarding.country')}</label>
          <select
            className="form-input"
            value={data.countryCode || 'NO'}
            onChange={(e) => onChange({ countryCode: e.target.value })}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {countryNames[country.code] || country.code}
              </option>
            ))}
          </select>
          <div className="form-hint">{t('onboarding.countryHint')}</div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('onboarding.orgNumber')}</label>
          <input
            className="form-input"
            type="text"
            placeholder="123 456 789"
            value={data.orgNumber || ''}
            onChange={(e) => onChange({ orgNumber: e.target.value })}
          />
          <div className="form-hint">{t('onboarding.orgNumberHint')}</div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid var(--color-gray-200)', margin: '1.5rem 0' }} />

      <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: '1rem', color: 'var(--color-gray-700)' }}>
        {t('onboarding.appearance')}
      </h3>

      <div className="form-group">
        <label className="form-label">{t('onboarding.layout')}</label>
        <div className="layout-picker">
          {layouts.map((layout) => {
            const selected = (data.template || 'classic') === layout.key
            return (
              <button
                type="button"
                key={layout.key}
                className={`layout-card ${selected ? 'layout-card--selected' : ''}`}
                onClick={() => onChange({ template: layout.key })}
                aria-pressed={selected}
              >
                <span className={`layout-card__thumb layout-card__thumb--${layout.key}`} aria-hidden="true">
                  <span className="layout-card__thumb-bar" />
                  <span className="layout-card__thumb-body" />
                </span>
                <span className="layout-card__name">{layout.name}</span>
                <span className="layout-card__desc">{layout.description}</span>
              </button>
            )
          })}
        </div>
        <div className="form-hint">{t('onboarding.layoutHint')}</div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('onboarding.tagline')}</label>
        <input
          className="form-input"
          type="text"
          maxLength={200}
          placeholder={t('onboarding.taglinePlaceholder')}
          value={data.tagline || ''}
          onChange={(e) => onChange({ tagline: e.target.value })}
        />
        <div className="form-hint">{t('onboarding.taglineHint')}</div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('onboarding.logo')}</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handleLogoFile(e.target.files?.[0])}
        />
        {data.logoPreview ? (
          <div className="logo-upload logo-upload--has-image">
            <img src={data.logoPreview} alt="Logo" className="logo-upload__preview" />
            <div className="logo-upload__actions">
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => fileInputRef.current?.click()}>
                {t('onboarding.logoChange')}
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={clearLogo}>
                {t('onboarding.logoRemove')}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="logo-upload" onClick={() => fileInputRef.current?.click()}>
            <span className="logo-upload__icon">🖼️</span>
            <span>{t('onboarding.logoUpload')}</span>
          </button>
        )}
        {errors.logo && <div className="form-error">{errors.logo}</div>}
        <div className="form-hint">{t('onboarding.logoHint')}</div>

        {!data.logoPreview && (
          <details style={{ marginTop: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
              {t('onboarding.logoUseUrl')}
            </summary>
            <input
              className="form-input"
              type="url"
              placeholder={t('onboarding.logoUrlPlaceholder')}
              value={data.logoUrl || ''}
              onChange={(e) => onChange({ logoUrl: e.target.value })}
              style={{ marginTop: '0.5rem' }}
            />
            <div className="form-hint">{t('onboarding.logoUrlHint')}</div>
          </details>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">{t('onboarding.subscriptionPlan')}</label>
        <select
          className="form-input"
          value={data.subscriptionPlan || 'free'}
          onChange={(e) => onChange({ subscriptionPlan: e.target.value })}
        >
          {(plans.length > 0 ? plans : [{ planKey: 'free', name: 'Free', monthlyPrice: 0, currency: 'NOK' }]).map((plan) => (
            <option key={plan.planKey} value={plan.planKey}>
              {plan.name} ({plan.monthlyPrice} {plan.currency})
            </option>
          ))}
        </select>
        <div className="form-hint">{t('onboarding.subscriptionPlanHint')}</div>
      </div>

      <div className="wizard__actions">
        <button className="btn btn--secondary" onClick={onBack}>
          {t('onboarding.back')}
        </button>
        <button className="btn btn--primary" onClick={handleNext}>
          {t('onboarding.next')}
        </button>
      </div>
    </div>
  )
}
